import { useLazyQuery, useQuery, useSuspenseQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import {
  FETCH_ASSESSMENT_PAGE,
  AssessmentPageQuery,
  AssessmentPageQueryVariables,
  Cohort,
  FetchCohortsBySchoolAndSchoolYearQuery,
  FetchCohortsBySchoolAndSchoolYearQueryVariables,
  Mission,
  FETCH_YEAR_GROUPS,
  FetchYearGroupsQuery,
  FetchYearGroupsQueryVariables,
  FetchKeyStagesQuery,
  FetchKeyStagesQueryVariables,
  FETCH_KEY_STAGES,
  FETCH_COHORTS_BY_SCHOOL_AND_SCHOOL_YEAR,
} from '@edwin-group/cj-graphql';
import { useAssessmentStore } from '../store/useAssessmentStore';
import { GetCurrentSchoolYearUseCase } from '../../use-cases/get-current-school-year';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useChapter } from '../../shared/hooks/useChapter';

export const useRoot = () => {
  const { missionResult, errors: chapterErrors } = useChapter();
  const authentication = useAuthentication();

  const assessmentPage = useAssessmentStore((state) => state.assessmentPage);
  const cohorts = useAssessmentStore((state) => state.cohorts);
  const setCohorts = useAssessmentStore((state) => state.setCohorts);
  const setAssessmentPage = useAssessmentStore((state) => state.setAssessmentPage);

  const { data: assessmentPageResult, error: assessmentPageError } = useSuspenseQuery<
    AssessmentPageQuery,
    AssessmentPageQueryVariables
  >(FETCH_ASSESSMENT_PAGE, {
    fetchPolicy: 'cache-and-network',
  });

  const [fetchCohorts, { data: cohortsResult, error: cohortsError }] = useLazyQuery<
    FetchCohortsBySchoolAndSchoolYearQuery,
    FetchCohortsBySchoolAndSchoolYearQueryVariables
  >(FETCH_COHORTS_BY_SCHOOL_AND_SCHOOL_YEAR, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: yearGroupsResult, error: yearGroupsError } = useQuery<
    FetchYearGroupsQuery,
    FetchYearGroupsQueryVariables
  >(FETCH_YEAR_GROUPS, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: keyStagesResult, error: keyStagesError } = useQuery<FetchKeyStagesQuery, FetchKeyStagesQueryVariables>(
    FETCH_KEY_STAGES,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (missionResult) {
      const mission = missionResult?.fetchMissionBySlugAndSchoolAndSchoolYear as Mission;
      fetchCohorts({
        variables: {
          schoolDocumentId: authentication?.schoolContext?.documentId || '',
          schoolYear: GetCurrentSchoolYearUseCase.execute(),
          missionDocumentId: mission?.documentId || '',
        },
      });
    }
  }, [missionResult, authentication?.schoolContext?.documentId, fetchCohorts]);

  useEffect(() => {
    if (assessmentPageResult) {
      setAssessmentPage(assessmentPageResult);
    }
  }, [assessmentPageResult, setAssessmentPage]);

  useEffect(() => {
    if (cohortsResult?.fetchCohortsBySchoolAndSchoolYear) {
      setCohorts(cohortsResult.fetchCohortsBySchoolAndSchoolYear as Cohort[]);
    }
  }, [cohortsResult, setCohorts]);

  const noCohortConfigured = useMemo(() => cohorts?.length === 0, [cohorts]);

  return {
    assessmentPage,
    yearGroupsResult,
    keyStagesResult,
    errors: [assessmentPageError, cohortsError, yearGroupsError, keyStagesError, ...chapterErrors].filter(Boolean),
    noCohortConfigured,
  };
};
